/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
import { Box, Button, FormHelperText, Link as MuiLink } from "@mui/material";
import { Form, Formik, FormikHelpers } from "formik";
import Link from "next/link";
import { useRouter } from "next/router";
import { useSnackbar } from "notistack";
import { useResendConfirmationEmailMutation } from "queries/useResendConfirmationEmail";
import React, { FC, lazy, Suspense, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import { FormikTextField } from "components/formik/FormikTextField";

import { ROUTES } from "constants/routes";
import useAuth from "hooks/useAuth";
import useIsMountedRef from "hooks/useIsMountedRef";
import useMFA from "hooks/useMFA";

// Lazy load MFA modals for better performance
const MFASetupModal = lazy(async () => {
  const mfaSetupModule = await import(
    "components/authentication/mfa/MFASetupModal"
  );
  return { default: mfaSetupModule.MFASetupModal };
});
const MFAVerificationModal = lazy(async () => {
  const mfaVerificationModule = await import(
    "components/authentication/mfa/MFAVerificationModal"
  );
  return { default: mfaVerificationModule.MFAVerificationModal };
});

export interface LoginFormState {
  email: string;
  password: string;
  submit?: string;
}

const initialValues: LoginFormState = {
  email: "",
  password: "",
};

export const LoginForm: FC = (props) => {
  const isMountedRef = useIsMountedRef();
  const { i18n } = useTranslation();
  const { login, completeMFALogin, mfaState } = useAuth();
  const { verifyCode } = useMFA();
  const router = useRouter();

  const [resendEmail, setResendEmail] = React.useState("");
  const [showResendEmail, setShowResendEmail] = React.useState(false);
  const [resendEmailConfirmation] = useResendConfirmationEmailMutation();
  const { enqueueSnackbar } = useSnackbar();

  // MFA modal state
  const [showMFASetup, setShowMFASetup] = useState(false);
  const [showMFAVerification, setShowMFAVerification] = useState(false);
  const [isCompletingLogin, setIsCompletingLogin] = useState(false);

  const loginFormSchema: Yup.SchemaOf<LoginFormState> = Yup.object({
    email: Yup.string().required(i18n.t("login.email.error")),
    password: Yup.string().required(i18n.t("login.password.error")),
    submit: Yup.string(),
  });

  const handleSubmitLogin = async (
    values: LoginFormState,
    { setErrors, setSubmitting }: FormikHelpers<LoginFormState>
  ) => {
    try {
      const mfaResult = await login({
        email: values.email,
        password: values.password,
      });

      if (isMountedRef.current) {
        // Check if MFA is required
        if (mfaResult.requiresSetup) {
          // Show MFA setup modal
          setShowMFASetup(true);
          setSubmitting(false);
        } else if (mfaResult.requiresMFA) {
          // Show MFA verification modal
          setShowMFAVerification(true);
          setSubmitting(false);
        } else {
          // No MFA required, proceed to dashboard
          router.push(ROUTES.DASHBOARD);
          setSubmitting(false);
        }
      }
    } catch (err: unknown) {
      if (isMountedRef.current) {
        setErrors({ submit: (err as Error).message });
        setResendEmail(values.email);
        if (
          (err as Error).message ===
          "You must confirm your email before logging in"
        )
          setShowResendEmail(true);
        setSubmitting(false);
      }
    }
  };

  const tryResendConfirmationEmail = async () => {
    if (resendEmail)
      try {
        await resendEmailConfirmation({
          variables: { email: resendEmail },
        });
        enqueueSnackbar("Resent!", { variant: "success" });
      } catch (err: unknown) {
        enqueueSnackbar("An Error Occurred", { variant: "error" });
      }
  };

  /**
   * Handle MFA setup completion
   * Called when user successfully verifies their TOTP code during setup
   */
  const handleMFASetupComplete = useCallback(() => {
    // Close setup modal
    setShowMFASetup(false);

    // User needs to log in again with their new MFA code
    enqueueSnackbar(
      "MFA setup complete! Please log in with your password and authenticator code.",
      {
        variant: "info",
      }
    );
  }, [enqueueSnackbar]);

  /**
   * Handle MFA verification completion
   * Called when user successfully verifies their TOTP code during login
   */
  const handleMFAVerificationComplete = useCallback(
    async (code: string): Promise<void> => {
      // Get username and session from mfaState
      const username = mfaState.username;
      const session = mfaState.mfaSession;

      if (!username || !session) {
        throw new Error("Missing username or session for MFA verification");
      }

      const result = await verifyCode({
        username,
        code,
        session,
      });

      if (!result.success) {
        throw new Error("MFA verification failed");
      }

      /*
       * Store session tokens after successful MFA verification
       */
      if (result.sessionTokens) {
        localStorage.setItem("access-token", result.sessionTokens.access);
        localStorage.setItem("refresh-token", result.sessionTokens.refresh);
        localStorage.setItem("id-token", result.sessionTokens.id);
      }

      /*
       * Show completing login state in modal
       */
      setIsCompletingLogin(true);

      /*
       * Complete login flow - update auth context and redirect to dashboard
       */
      await completeMFALogin();

      // Close verification modal
      setShowMFAVerification(false);
      setIsCompletingLogin(false);

      enqueueSnackbar("Login successful", { variant: "success" });

      // Small delay to ensure auth context propagates before navigation
      await new Promise((resolve) => {
        setTimeout(resolve, 100);
      });
      void router.push(ROUTES.DASHBOARD);
    },
    [verifyCode, completeMFALogin, router, enqueueSnackbar, mfaState]
  );

  /**
   * Handle MFA modal cancellation
   */
  const handleMFACancel = useCallback(() => {
    setShowMFASetup(false);
    setShowMFAVerification(false);
  }, []);

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={loginFormSchema}
        onSubmit={handleSubmitLogin}
      >
        {({ errors, isSubmitting }) => (
          <Form {...props}>
            <FormikTextField
              name="email"
              autoFocus
              autoComplete="username"
              fullWidth
              label={i18n.t("login.email.useremail") as string}
              margin="normal"
              type="username"
            />

            <FormikTextField
              name="password"
              autoComplete="password"
              fullWidth
              label={i18n.t("login.password.label") as string}
              margin="normal"
              type="password"
            />

            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Link href={ROUTES.AUTH_FORGOT_PASSWORD} passHref>
                <MuiLink sx={{ pt: 1 }}>
                  {i18n.t("login.forgot_password") as string}
                </MuiLink>
              </Link>
            </Box>

            {errors.submit && (
              <Box sx={{ mt: 3 }}>
                <FormHelperText error>{errors.submit}</FormHelperText>
              </Box>
            )}
            {showResendEmail ? (
              <Box sx={{ mt: 2 }}>
                <Button
                  color="secondary"
                  fullWidth
                  size="large"
                  variant="contained"
                  onClick={() => {
                    tryResendConfirmationEmail();
                  }}
                >
                  Resend my verification email
                </Button>
              </Box>
            ) : null}

            <Box sx={{ mt: 2 }}>
              <Button
                disabled={isSubmitting}
                fullWidth
                size="large"
                type="submit"
                variant="contained"
                color="secondary"
              >
                {i18n.t("login.loginbutton") as string}
              </Button>
            </Box>
          </Form>
        )}
      </Formik>

      {/* MFA Setup Modal */}
      <Suspense fallback={null}>
        <MFASetupModal
          open={showMFASetup}
          onClose={handleMFACancel}
          onComplete={handleMFASetupComplete}
          mfaSession={mfaState.mfaSession}
        />
      </Suspense>

      {/* MFA Verification Modal */}
      <Suspense fallback={null}>
        <MFAVerificationModal
          open={showMFAVerification}
          onVerify={handleMFAVerificationComplete}
          onCancel={handleMFACancel}
          isCompletingLogin={isCompletingLogin}
        />
      </Suspense>
    </>
  );
};
